import axios from 'axios';
import { supabase } from './supabaseClient';

// Constants
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
const CACHE_VERSION = 'v4';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const API_BASE_URL =
  import.meta.env.VITE_IMPORT_EXPORT_API_URL ||
  (SUPABASE_URL ? `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/import-export` : '/api/import-export');
const KENYA_BUREAU_API = 'https://www.knbs.or.ke/api'; // Kenya National Bureau of Statistics
const UN_COMTRADE_API = 'https://comtrade.un.org/api/get'; // UN Comtrade API
const WORLD_BANK_API = 'https://api.worldbank.org/v2'; // World Bank API

const apiClient = axios.create();

if (SUPABASE_ANON_KEY) {
  apiClient.defaults.headers.common.apikey = SUPABASE_ANON_KEY;
  apiClient.defaults.headers.common.Authorization = `Bearer ${SUPABASE_ANON_KEY}`;
}

apiClient.interceptors.request.use(async (config) => {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) {
      config.headers = config.headers || {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
  } catch {
  }
  return config;
});

// Interface definitions
export interface ImportData {
  source: string;
  quantity: number;
  value: number;
  unit: string;
  timestamp: string;
}

export interface ExportData {
  destination: string;
  quantity: number;
  value: number;
  unit: string;
  timestamp: string;
}

export interface TradeRegulation {
  category: string;
  description: string;
  documentRequired: string[];
  lastUpdated: string;
  link: string;
}

export interface ImportExportAnalysis {
  totalExportValue: number;
  totalImportValue: number;
  tradeBalance: number;
  yearOnYearGrowth: number | null;
  tradeVolume: number;
  topExports: {
    commodity: string;
    destination: string;
    value: number;
    quantity: number;
  }[];
  topImports: {
    commodity: string;
    source: string;
    value: number;
    quantity: number;
  }[];
  regulations: TradeRegulation[];
  dataSource: string;
}

export interface MarketOpportunity {
  country: string;
  commodity: string;
  price: string;
  currency: string;
  growth: number;
  demand: number;
  hsCode: string;
  description: string;
}

export interface TimeSeriesData {
  sourceGranularity: 'yearly';
  yearly: {
    labels: string[];
    exports: number[];
    imports: number[];
  };
}

interface CommodityTrade {
  analysis: {
    totalExportValue: number;
    totalImportValue: number;
    tradeBalance: number;
    yearOnYearChange: number | null;
    exportVolume: number;
    importVolume: number;
  };
  exports: ExportData[];
  imports: ImportData[];
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Cache for storing API responses
type CacheStore = {
  [key: string]: CacheEntry<any>;
};

class ImportExportService {
  private cache: CacheStore = {};
  private availableYears: number[] = [];
  private maxDataYear: number;

  constructor() {
    const currentYear = new Date().getFullYear();
    this.maxDataYear = currentYear - 2;

    for (let year = this.maxDataYear; year >= this.maxDataYear - 10; year--) {
      this.availableYears.push(year);
    }
  }

  private normalizeYear(year: number): number {
    if (!Number.isFinite(year)) return this.maxDataYear;
    return Math.min(year, this.maxDataYear);
  }

  private isAllZeroTimeSeries(data: TimeSeriesData): boolean {
    const all = [
      ...data.yearly.exports,
      ...data.yearly.imports,
    ];
    return all.every((v) => !v || v === 0);
  }

  /**
   * Get global market opportunities for Kenya's major exports
   */
  async getMarketOpportunities(): Promise<MarketOpportunity[]> {
    const cacheKey = `${CACHE_VERSION}_market_opportunities`;
    const cachedData = this.checkCache<MarketOpportunity[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const year = this.maxDataYear;
    const response = await apiClient.get(`${API_BASE_URL}/market-opportunities`, { params: { year } });
    if (!Array.isArray(response.data)) throw new Error('Market opportunities API returned unexpected data.');
    this.setCache(cacheKey, response.data);
    return response.data;
  }

  /**
   * Get the list of available years for data
   */
  getAvailableYears(): number[] {
    return this.availableYears;
  }

  clearCache(): void {
    this.cache = {};
  }

  getCommodityCodeForName(commodity: string): string {
    return this.getCommodityCode(commodity);
  }

  async getRawComtrade(params: { year: number; reporter: string; partner: string; commodity: string; [key: string]: any }): Promise<{ dataset: any[]; meta?: any }> {
    const response = await apiClient.get(`${API_BASE_URL}/comtrade`, { params });
    if (!response?.data || !Array.isArray(response.data.dataset)) {
      throw new Error('Comtrade API returned no dataset.');
    }
    return { dataset: response.data.dataset, meta: response.data.meta };
  }

  /**
   * Get comprehensive trade analysis for a given country
   * @param year Year to get data for
   */
  async getCountryTradeAnalysis(year: number): Promise<ImportExportAnalysis> {
    const normalizedYear = this.normalizeYear(year);
    const cacheKey = `${CACHE_VERSION}_country_trade_${normalizedYear}`;
    
    // Check cache first
    const cachedData = this.checkCache<ImportExportAnalysis>(cacheKey);
    if (cachedData) {
      console.log(`Using cached country trade data for year ${normalizedYear}`);
      return cachedData;
    }
    
    try {
      console.log(`Fetching trade data for year ${normalizedYear}`);
      
      // If scraping fails, use the proxy to UN Comtrade
      const response = await apiClient.get(`${API_BASE_URL}/comtrade`, {
        params: {
          year: normalizedYear,
          reporter: 'KEN',
          partner: 'all',
          commodity: 'TOTAL'
        }
      });
      
      let data: ImportExportAnalysis;
      
      if (response.data && response.data.dataset) {
        // Process real data from API
        console.log('Processing real trade data from UN Comtrade');
        data = this.processComtradeData(response.data, normalizedYear);
      } else {
        throw new Error('Trade data API returned no dataset.');
      }
      
      // Cache the result
      this.setCache(cacheKey, data);
      
      return data;
    } catch (error) {
      console.error('Error fetching country trade analysis:', error);
      throw error;
    }
  }
  
  /**
   * Process data from scraped source
   */
  private processScrapedData(scrapedData: any, year: number): ImportExportAnalysis {
    try {
      // This is a simplified example - real implementation would process actual data structure
      const totalExports = scrapedData.exports.reduce((sum: number, item: any) => sum + item.value, 0);
      const totalImports = scrapedData.imports.reduce((sum: number, item: any) => sum + item.value, 0);
      
      return {
        totalExportValue: totalExports,
        totalImportValue: totalImports,
        tradeBalance: totalExports - totalImports,
        yearOnYearGrowth: null,
        tradeVolume: totalExports + totalImports,
        topExports: scrapedData.exports.slice(0, 10),
        topImports: scrapedData.imports.slice(0, 10),
        regulations: [],
        dataSource: 'Kenya National Bureau of Statistics'
      };
    } catch (error) {
      console.error('Error processing scraped data:', error);
      throw error;
    }
  }
  
  /**
   * Process data from UN Comtrade API
   */
  private processComtradeData(apiData: any, year: number): ImportExportAnalysis {
    try {
      const dataset = apiData.dataset || [];
      let totalExports = 0;
      let totalImports = 0;
      
      // Aggregate exports by destination
      const exportDestinations: Map<string, { value: number, quantity: number }> = new Map();
      // Aggregate imports by source
      const importSources: Map<string, { value: number, quantity: number }> = new Map();
      
      // Process dataset
      dataset.forEach((item: any) => {
        const valueUsd = item.TradeValue || 0;
        const qty = item.qty || 0;
        const partner = item.ptTitle || (typeof item.partnerCode !== 'undefined' ? `Partner ${item.partnerCode}` : 'Unknown');
        
        if (item.rgDesc === 'Export') {
          totalExports += valueUsd;
          const current = exportDestinations.get(partner) || { value: 0, quantity: 0 };
          exportDestinations.set(partner, {
            value: current.value + valueUsd,
            quantity: current.quantity + qty
          });
        } else if (item.rgDesc === 'Import') {
          totalImports += valueUsd;
          const current = importSources.get(partner) || { value: 0, quantity: 0 };
          importSources.set(partner, {
            value: current.value + valueUsd,
            quantity: current.quantity + qty
          });
        }
      });

      if (totalExports === 0 && totalImports === 0) {
        throw new Error(`No Comtrade data returned for year ${year}.`);
      }

      // Build top exports (by destination) and top imports (by source)
      const topExports: any[] = Array.from(exportDestinations.entries())
        .map(([dest, data]) => ({
          commodity: 'All commodities',
          destination: dest,
          value: data.value,
          quantity: data.quantity
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);

      const topImports: any[] = Array.from(importSources.entries())
        .map(([source, data]) => ({
          commodity: 'All commodities',
          source: source,
          value: data.value,
          quantity: data.quantity
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);
      
      return {
        totalExportValue: totalExports,
        totalImportValue: totalImports,
        tradeBalance: totalExports - totalImports,
        yearOnYearGrowth: null,
        tradeVolume: totalExports + totalImports,
        topExports,
        topImports,
        regulations: this.getKenyaTradeRegulations(),
        dataSource: 'UN Comtrade Database'
      };
    } catch (error) {
      console.error('Error processing Comtrade data:', error);
      throw error;
    }
  }

  /**
   * Get trade data for a specific commodity
   * @param commodity The commodity name
   * @param year The year to get data for
   */
  async getCommodityTradeData(commodity: string, year: number): Promise<CommodityTrade> {
    const normalizedYear = this.normalizeYear(year);
    const cacheKey = `${CACHE_VERSION}_commodity_${commodity}_${normalizedYear}`;
    
    // Check cache first
    const cachedData = this.checkCache<CommodityTrade>(cacheKey);
    if (cachedData) {
      console.log(`Using cached commodity data for ${commodity}, year ${normalizedYear}`);
      return cachedData;
    }
    
    try {
      console.log(`Fetching commodity data for ${commodity}, year ${normalizedYear}`);
      
      // Get commodity HS code
      const commodityCode = this.getCommodityCode(commodity);
      
      // If scraping fails, use proxy to UN Comtrade
      const response = await apiClient.get(`${API_BASE_URL}/comtrade`, {
        params: {
          year: normalizedYear,
          reporter: 'KEN',
          partner: 'all',
          commodity: commodityCode
        }
      });
      
      let data: CommodityTrade;
      
      if (response.data && response.data.dataset) {
        // Process real data from API
        console.log(`Processing real commodity data for ${commodity}`);
        data = this.processCommodityData(response.data, commodity, normalizedYear);
      } else {
        throw new Error(`Trade data API returned no dataset for commodity "${commodity}".`);
      }
      
      // Cache the result
      this.setCache(cacheKey, data);
      
      return data;
    } catch (error) {
      console.error(`Error fetching trade data for ${commodity}:`, error);
      throw error;
    }
  }
  
  /**
   * Process commodity data from API
   */
  private processCommodityData(apiData: any, commodity: string, year: number): CommodityTrade {
    try {
      const dataset = apiData.dataset || [];
      const exports: ExportData[] = [];
      const imports: ImportData[] = [];
      let totalExportValue = 0;
      let totalImportValue = 0;
      let exportVolume = 0;
      let importVolume = 0;

      dataset.forEach((item: any) => {
        const valueUsd = item.TradeValue || 0;
        const qty = item.qty || 0;
        const unit = item.qtDesc || 'kg';
        const partner = item.ptTitle || (typeof item.partnerCode !== 'undefined' ? `Partner ${item.partnerCode}` : 'Unknown');
        const period = String(item.Period) || String(year);

        if (item.rgDesc === 'Export') {
          totalExportValue += valueUsd;
          exportVolume += qty;
          exports.push({
            destination: partner,
            value: valueUsd,
            quantity: qty,
            unit: unit,
            timestamp: period
          });
        } else if (item.rgDesc === 'Import') {
          totalImportValue += valueUsd;
          importVolume += qty;
          imports.push({
            source: partner,
            value: valueUsd,
            quantity: qty,
            unit: unit,
            timestamp: period
          });
        }
      });

      if (exports.length === 0 && imports.length === 0) throw new Error(`No Comtrade commodity data returned for "${commodity}" in ${year}.`);

      // Sort by value
      exports.sort((a, b) => b.value - a.value);
      imports.sort((a, b) => b.value - a.value);

      return {
        analysis: {
          totalExportValue,
          totalImportValue,
          tradeBalance: totalExportValue - totalImportValue,
          yearOnYearChange: null,
          exportVolume,
          importVolume
        },
        exports,
        imports
      };
    } catch (error) {
      console.error('Error processing commodity data:', error);
      throw error;
    }
  }

  /**
   * Get time series data for imports and exports
   */
  async getTimeSeriesData(startYear: number, endYear: number): Promise<TimeSeriesData> {
    const normalizedEnd = Math.min(endYear, this.maxDataYear);
    const normalizedStart = Math.min(startYear, normalizedEnd);
    const cacheKey = `${CACHE_VERSION}_timeseries_${normalizedStart}_${normalizedEnd}`;
    
    // Check cache first
    const cachedData = this.checkCache<TimeSeriesData>(cacheKey);
    if (cachedData && !this.isAllZeroTimeSeries(cachedData)) {
      console.log(`Using cached time series data for years ${normalizedStart}-${normalizedEnd}`);
      return cachedData;
    }
    
    try {
      console.log(`Fetching time series data for years ${normalizedStart}-${normalizedEnd}`);
      
      // Use proxy to World Bank API
      const response = await apiClient.get(`${API_BASE_URL}/worldbank/NE.EXP.GNFS.CD;NE.IMP.GNFS.CD`, {
        params: {
          startYear: normalizedStart,
          endYear: normalizedEnd
        }
      });
      
      let data: TimeSeriesData;
      
      if (!response.data) throw new Error('World Bank API returned no time series dataset.');

      console.log('Processing real time series data from World Bank');
      data = this.processTimeSeriesData(response.data, normalizedStart, normalizedEnd);
      
      // Cache the result
      this.setCache(cacheKey, data);
      
      return data;
    } catch (error) {
      console.error('Error fetching time series data:', error);
      throw error;
    }
  }
  
  /**
   * Process time series data from World Bank API
   */
  private processTimeSeriesData(apiData: any, startYear: number, endYear: number): TimeSeriesData {
    try {
      const extractSeries = (input: any): any[] => {
        if (Array.isArray(input) && Array.isArray(input[1])) return input[1];
        if (Array.isArray(input?.data) && Array.isArray(input.data[1])) return input.data[1];
        if (Array.isArray(input?.result)) return input.result;
        if (Array.isArray(input?.items)) return input.items;
        if (Array.isArray(input) && input.length > 0 && input[0]?.date && input[0]?.indicator) return input;
        return [];
      };

      const yearlyLabels: string[] = [];
      const yearlyExports: number[] = [];
      const yearlyImports: number[] = [];
      
      const data = extractSeries(apiData);

      // Map of year to data for easy access
      const yearDataMap: Map<string, {exports: number, imports: number}> = new Map();
      
      // Process all data points
      let hasValidData = false;
      if (Array.isArray(data) && data.length > 0) {
        data.forEach((item: any) => {
          const year = item.date;
          const value = item.value || 0;
          
          if (!year) return;
          if (!item?.indicator?.id) return;

          if (!yearDataMap.has(year)) {
            yearDataMap.set(year, {exports: 0, imports: 0});
          }
          
          const yearData = yearDataMap.get(year)!;
          
          if (item.indicator.id === 'NE.EXP.GNFS.CD') {
            yearData.exports = value;
            hasValidData = true;
          } else if (item.indicator.id === 'NE.IMP.GNFS.CD') {
            yearData.imports = value;
            hasValidData = true;
          }
        });
      }
      
      if (!hasValidData) {
        throw new Error(`World Bank time series data missing for ${startYear}-${endYear}.`);
      }

      // Create labels and data arrays in ascending order. The current World Bank
      // series is annual, so we only expose yearly points instead of synthesized
      // quarterly or monthly estimates.
      for (let year = startYear; year <= endYear; year++) {
        const yearStr = year.toString();
        yearlyLabels.push(yearStr);
        
        const yearData = yearDataMap.get(yearStr) || {exports: 0, imports: 0};
        yearlyExports.push(yearData.exports);
        yearlyImports.push(yearData.imports);
      }
      
      return {
        sourceGranularity: 'yearly',
        yearly: {
          labels: yearlyLabels,
          exports: yearlyExports,
          imports: yearlyImports
        }
      };
    } catch (error) {
      console.error('Error processing time series data:', error);
      throw error;
    }
  }
  
  /**
   * Get trade regulations for a specific country
   */
  async getTradeRegulations(country: string): Promise<TradeRegulation[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/regulations/${country}`).catch(() => ({ data: null }));
      
      if (response.data && !response.data.error) {
        return response.data;
      }
      
      if (String(country || '').toLowerCase() === 'kenya') return this.getKenyaTradeRegulations();
      return this.getKenyaTradeRegulations();
    } catch (error) {
      console.error('Error fetching trade regulations:', error);
      if (String(country || '').toLowerCase() === 'kenya') return this.getKenyaTradeRegulations();
      return this.getKenyaTradeRegulations();
    }
  }

  /**
   * Get commodity HS code for API requests
   * Maps common commodities to their HS codes
   */
  private getCommodityCode(commodity: string): string {
    const commodityCodes: {[key: string]: string} = {
      'tea': '0902', // Tea
      'coffee': '0901', // Coffee
      'cut flowers': '0603', // Cut flowers
      'fresh vegetables': '07', // Vegetables
      'fresh fruits': '08', // Fruits
      'maize': '1005', // Maize (corn)
      'wheat': '1001', // Wheat
      'rice': '1006', // Rice
      'beans': '0713', // Dried leguminous vegetables
      'potatoes': '0701', // Potatoes
      'sugarcane': '1701', // Cane sugar
      'avocados': '080440', // Avocados
      'mangoes': '080450', // Mangoes
      'macadamia nuts': '080262', // Macadamia nuts
      'cashew nuts': '080131', // Cashew nuts
      'pineapples': '080430', // Pineapples
      'sisal': '5304', // Sisal
      'cotton': '5201', // Cotton
      'pyrethrum': '1211', // Plants used in perfumery, pharmacy
      'tobacco': '24', // Tobacco
      'dairy products': '04', // Dairy produce
      'meat products': '02', // Meat
      'fish products': '03', // Fish
      'leather products': '42', // Leather articles
      'titanium ores': '2614', // Titanium ores
      'petroleum products': '27', // Mineral fuels, oils
      'chemicals': '28', // Inorganic chemicals
      'pharmaceuticals': '30', // Pharmaceutical products
      'textile products': '63', // Textile articles
      'cement': '2523', // Cement
      'machinery': '84', // Machinery
      'vehicles': '87', // Vehicles
      'electrical equipment': '85' // Electrical machinery
    };
    
    return commodityCodes[commodity] || 'TOTAL'; // Default to total if commodity not found
  }
  
  /**
   * Check if data exists in cache and is still valid
   * @param key Cache key
   */
  private checkCache<T>(key: string): T | null {
    const cached = this.cache[key];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  /**
   * Set data in cache
   * @param key Cache key
   * @param data Data to cache
   */
  private setCache<T>(key: string, data: T): void {
    this.cache[key] = {
      data,
      timestamp: Date.now()
    };
  }

  private getKenyaTradeRegulations(): TradeRegulation[] {
    const lastUpdated = new Date().toISOString().slice(0, 10);
    return [
      {
        category: 'Import Clearance (KRA Customs)',
        description:
          'Most imports require customs declaration and clearance through Kenya Revenue Authority (KRA). Requirements vary by product, origin, and mode of transport.',
        documentRequired: [
          'Commercial Invoice',
          'Packing List',
          'Bill of Lading / Air Waybill',
          'Import Declaration Form (IDF) (where applicable)',
          'Taxpayer identification / KRA PIN details',
        ],
        lastUpdated,
        link: 'https://www.kra.go.ke/en/business/companies-partnerships/companies-partnerships-pin-taxes/companies-partnerships-taxes/customs-duty',
      },
      {
        category: 'Import Standards Compliance (KEBS PVoC/CoC)',
        description:
          'Many regulated imports must comply with Kenya Bureau of Standards (KEBS) requirements. Pre-Export Verification of Conformity (PVoC) and a Certificate of Conformity (CoC) may be required for clearance.',
        documentRequired: [
          'Pre-Export Verification of Conformity (PVoC) documentation (where applicable)',
          'Certificate of Conformity (CoC) (where applicable)',
          'Product labels/markings as required',
          'Commercial Invoice',
        ],
        lastUpdated,
        link: 'https://www.kebs.org/',
      },
      {
        category: 'Import SPS / Permits (Agricultural & Food Goods)',
        description:
          'Plant, animal, and food imports may require sanitary and phytosanitary (SPS) clearance, permits, and inspection. The exact permit/certificate depends on the commodity (e.g., plant products vs. animal products).',
        documentRequired: [
          'Import permit / authorization (commodity-specific)',
          'Phytosanitary certificate (for plant/plant products, where applicable)',
          'Health/veterinary certificate (for animal products, where applicable)',
          'Commercial Invoice',
        ],
        lastUpdated,
        link: 'https://www.kephis.org/',
      },
      {
        category: 'Export Clearance (KRA Customs)',
        description:
          'Exports typically require customs declaration and supporting documents. Some goods may also require export permits or licenses depending on the commodity and destination.',
        documentRequired: [
          'Export declaration / customs entry (through the relevant system)',
          'Commercial Invoice',
          'Packing List',
          'Bill of Lading / Air Waybill',
          'Export permit/license (where applicable)',
        ],
        lastUpdated,
        link: 'https://www.kra.go.ke/',
      },
      {
        category: 'Export SPS / Phytosanitary (Agricultural Products)',
        description:
          'Agricultural exports often require inspection and certificates to meet destination-market SPS rules (e.g., phytosanitary certificates for plant products). Requirements vary by commodity and importing country.',
        documentRequired: [
          'Phytosanitary certificate (where applicable)',
          'Quality/inspection certificate (where applicable)',
          'Commercial Invoice',
          'Packing List',
        ],
        lastUpdated,
        link: 'https://www.kephis.org/',
      },
      {
        category: 'Certificate of Origin (Preferential Trade)',
        description:
          'A Certificate of Origin may be required by the buyer and/or to qualify for preferential tariffs under trade arrangements. The exact form depends on the agreement and destination.',
        documentRequired: [
          'Certificate of Origin (agreement-specific format)',
          'Commercial Invoice',
          'Packing List',
        ],
        lastUpdated,
        link: 'https://www.kentrade.go.ke/',
      },
    ];
  }

}

export const importExportService = new ImportExportService(); 
